package kz.dentalcrm.app.core.utils

import androidx.compose.runtime.Composable
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.month_eight
import dentalcrm.composeapp.generated.resources.month_eight_short
import dentalcrm.composeapp.generated.resources.month_eleven
import dentalcrm.composeapp.generated.resources.month_eleven_short
import dentalcrm.composeapp.generated.resources.month_five
import dentalcrm.composeapp.generated.resources.month_five_short
import dentalcrm.composeapp.generated.resources.month_four
import dentalcrm.composeapp.generated.resources.month_four_short
import dentalcrm.composeapp.generated.resources.month_nine
import dentalcrm.composeapp.generated.resources.month_nine_short
import dentalcrm.composeapp.generated.resources.month_one
import dentalcrm.composeapp.generated.resources.month_one_short
import dentalcrm.composeapp.generated.resources.month_seven
import dentalcrm.composeapp.generated.resources.month_seven_short
import dentalcrm.composeapp.generated.resources.month_six
import dentalcrm.composeapp.generated.resources.month_six_short
import dentalcrm.composeapp.generated.resources.month_ten
import dentalcrm.composeapp.generated.resources.month_ten_short
import dentalcrm.composeapp.generated.resources.month_three
import dentalcrm.composeapp.generated.resources.month_three_short
import dentalcrm.composeapp.generated.resources.month_twelve
import dentalcrm.composeapp.generated.resources.month_twelve_short
import dentalcrm.composeapp.generated.resources.month_two
import dentalcrm.composeapp.generated.resources.month_two_short
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.format
import kotlinx.datetime.format.char
import kotlinx.datetime.number
import kotlinx.datetime.plus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Instant

fun Month.nameRu(): String {
    return when (this) {
        Month.JANUARY -> "Январь"
        Month.FEBRUARY -> "Февраль"
        Month.MARCH -> "Март"
        Month.APRIL -> "Апрель"
        Month.MAY -> "Май"
        Month.JUNE -> "Июнь"
        Month.JULY -> "Июль"
        Month.AUGUST -> "Август"
        Month.SEPTEMBER -> "Сентябрь"
        Month.OCTOBER -> "Октябрь"
        Month.NOVEMBER -> "Ноябрь"
        Month.DECEMBER -> "Декабрь"
    }
}

/**
 * Formats a date string from backend format (ddmmyyyy) to display format (dd/MonthName/yyyy)
 * Example: "05121996" -> "05 Декабрь 1996"
 * @param monthNames List of 12 month names in order (January to December)
 */
fun String.formatBirthdayToDisplay(monthNames: List<String>): String {
    if (this.length != 8) return this
    if (monthNames.size != 12) return this
    
    return try {
        val day = this.substring(0, 2)
        val monthNumber = this.substring(2, 4).toInt()
        val year = this.substring(4, 8)
        
        val month = if (monthNumber in 1..12) {
            monthNames[monthNumber - 1]
        } else {
            monthNumber.toString()
        }
        
        "$day $month $year"
    } catch (e: Exception) {
        this
    }
}

/**
 * Formats LocalDate to backend format (ddmmyyyy)
 * Example: LocalDate(1996, 12, 5) -> "05121996"
 */
fun LocalDate.formatToBackend(): String {
    val day = day.toString().padStart(2, '0')
    val month = month.number.toString().padStart(2, '0')
    val year = year.toString()
    return "$day$month$year"
}

/**
 * Parses backend format (ddmmyyyy) to LocalDate
 * Example: "05121996" -> LocalDate(1996, 12, 5)
 */
fun String.parseBackendDate(): LocalDate? {
    if (this.length != 8) return null
    
    return try {
        val day = this.substring(0, 2).toInt()
        val month = this.substring(2, 4).toInt()
        val year = this.substring(4, 8).toInt()
        LocalDate(year, month, day)
    } catch (e: Exception) {
        null
    }
}

/**
 * Formats LocalDate to API format (yyyy-MM-dd)
 * Example: LocalDate(2004, 12, 5) -> "2004-12-05"
 */
fun LocalDate.formatToISO(): String {
    val day = day.toString().padStart(2, '0')
    val month = month.number.toString().padStart(2, '0')
    val year = year.toString()
    return "$year-$month-$day"
}

/**
 * Parses API format (yyyy-MM-dd) to LocalDate
 * Example: "2004-12-05" -> LocalDate(2004, 12, 5)
 */
fun String.parseISODate(): LocalDate? {
    if (this.length != 10) return null
    
    return try {
        val parts = this.split("-")
        if (parts.size != 3) return null
        val year = parts[0].toInt()
        val month = parts[1].toInt()
        val day = parts[2].toInt()
        LocalDate(year, month, day)
    } catch (e: Exception) {
        null
    }
}

@Composable
fun getMonths(): List<String> {
    return listOf(
        stringResource(Res.string.month_one),
        stringResource(Res.string.month_two),
        stringResource(Res.string.month_three),
        stringResource(Res.string.month_four),
        stringResource(Res.string.month_five),
        stringResource(Res.string.month_six),
        stringResource(Res.string.month_seven),
        stringResource(Res.string.month_eight),
        stringResource(Res.string.month_nine),
        stringResource(Res.string.month_ten),
        stringResource(Res.string.month_eleven),
        stringResource(Res.string.month_twelve)
    )
}

@Composable
fun getMonthsShort(): List<String> {
    return listOf(
        stringResource(Res.string.month_one_short),
        stringResource(Res.string.month_two_short),
        stringResource(Res.string.month_three_short),
        stringResource(Res.string.month_four_short),
        stringResource(Res.string.month_five_short),
        stringResource(Res.string.month_six_short),
        stringResource(Res.string.month_seven_short),
        stringResource(Res.string.month_eight_short),
        stringResource(Res.string.month_nine_short),
        stringResource(Res.string.month_ten_short),
        stringResource(Res.string.month_eleven_short),
        stringResource(Res.string.month_twelve_short)
    )
}

/**
 * Parses server date time format (yyyy-MM-dd HH:mm:ss) to milliseconds
 * Example: "2026-01-18 17:06:00" -> 1768755960000
 */
@OptIn(kotlin.time.ExperimentalTime::class)
fun String.parseServerDateTimeToMillis(): Long? {
    return try {
        val isoString = this.replace(" ", "T")
        val localDateTime = LocalDateTime.parse(isoString)
        localDateTime.toInstant(TimeZone.currentSystemDefault()).toEpochMilliseconds()
    } catch (e: Exception) {
        null
    }
}

/**
 * SQL-style date-time format with seconds (yyyy-MM-dd HH:mm:ss)
 * Example: "2026-02-08 18:55:00"
 */
val SqlStyleFormat = LocalDateTime.Format {
    year()
    char('-')
    monthNumber()
    char('-')
    day()
    char(' ')
    hour()
    char(':')
    minute()
    char(':')
    second()
}

/**
 * SQL-style date-time format without seconds (yyyy-MM-dd HH:mm)
 * Example: "2026-02-08 18:55"
 */
val SqlStyleFormatNoSeconds = LocalDateTime.Format {
    year()
    char('-')
    monthNumber()
    char('-')
    day()
    char(' ')
    hour()
    char(':')
    minute()
}

/**
 * Parses server date-time string to LocalDateTime
 * Supports both formats:
 * - With seconds: "2026-02-08 18:55:00"
 * - Without seconds: "2026-02-08 18:55"
 */
@OptIn(kotlin.time.ExperimentalTime::class)
fun String.parseServerDateTime(): LocalDateTime? {
    try {
        if (this.contains("T") && this.endsWith("Z")) {
            val instant = Instant.parse(this)
            return instant.toLocalDateTime(TimeZone.currentSystemDefault())
        }
    } catch (e: Exception) {
        // Continue to other formats
    }

    return try {
        // Try parsing with seconds first
        LocalDateTime.parse(this, SqlStyleFormat)
    } catch (e: Exception) {
        try {
            // Try parsing without seconds
            LocalDateTime.parse(this, SqlStyleFormatNoSeconds)
        } catch (e: Exception) {
            null
        }
    }
}

/**
 * Formats LocalDateTime to SQL string with seconds (yyyy-MM-dd HH:mm:ss)
 * Example: LocalDateTime -> "2026-02-08 18:55:00"
 */
fun LocalDateTime.toSqlString(): String {
    return this.format(SqlStyleFormat)
}

/**
 * Pads an integer with leading zeros to 2 digits
 * Example: 5 -> "05", 12 -> "12"
 */
fun Int.pad(): String = this.toString().padStart(2, '0')

/**
 * Adds minutes to LocalDateTime
 */
@OptIn(kotlin.time.ExperimentalTime::class)
fun LocalDateTime.plusMinutes(
    minutes: Int,
    timeZone: TimeZone = TimeZone.currentSystemDefault()
): LocalDateTime {
    return this.toInstant(timeZone)
        .plus(minutes, DateTimeUnit.MINUTE)
        .toLocalDateTime(timeZone)
}

/**
 * Formats server date-time string to inspection date format
 * Example: "2026-02-10 11:24:09" -> "10 фев, 2026 11:24"
 * @param monthNamesShort List of 12 short month names in order (January to December)
 */
fun String.formatToInspectionDate(monthNamesShort: List<String>): String? {
    if (monthNamesShort.size != 12) return null
    
    return try {
        val dateTime = this.parseServerDateTime() ?: return null
        val day = dateTime.day
        val monthIndex = dateTime.month.ordinal
        val year = dateTime.year
        val hour = dateTime.hour.pad()
        val minute = dateTime.minute.pad()
        
        val monthName = monthNamesShort.getOrNull(monthIndex) ?: return null
        "$day $monthName, $year $hour:$minute"
    } catch (e: Exception) {
        null
    }
}